import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  Paper,
  TextField,
  Typography,
} from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";

import BotonAtras from "../components/BotonAtras";
import { Cargando, EstadoVacio } from "../components/Estados";
import {
  guardarHorarioSolicitudes,
  obtenerHorarioSolicitudesGestion,
} from "../services/bibliotecaApi";
import { DIAS } from "../model/horarios";

const USOS = [
  {
    uso: "radicar",
    titulo: "Horario para radicar",
    descripcion: "Cuándo se puede enviar una solicitud nueva.",
  },
  {
    uso: "atencion",
    titulo: "Horario de atención",
    descripcion: "En qué días y horas se puede pedir la actividad (la fecha y las horas de la clase).",
  },
];

/** Los dias que manda el backend, a una fila por dia de la semana. */
const aSemana = (dias) =>
  DIAS.map(({ valor }) => {
    const abierto = dias.find(({ dia }) => dia === valor);
    return {
      dia: valor,
      abierto: Boolean(abierto),
      abre: abierto?.abre ?? "07:00",
      cierra: abierto?.cierra ?? "17:00",
    };
  });

/** Un horario (radicar o atencion): un renglon por dia, abierto o cerrado. */
const TablaHorario = ({ titulo, descripcion, texto, inicial, onGuardar }) => {
  const [semana, setSemana] = useState(() => aSemana(inicial));
  const [enviando, setEnviando] = useState(false);

  const cambiar = (dia, campo, valor) =>
    setSemana((actual) => actual.map((fila) => (fila.dia === dia ? { ...fila, [campo]: valor } : fila)));

  const malos = semana.filter(({ abierto, abre, cierra }) => abierto && (!abre || !cierra || abre >= cierra));

  const guardar = async () => {
    setEnviando(true);
    await onGuardar(
      semana.filter(({ abierto }) => abierto).map(({ dia, abre, cierra }) => ({ dia, abre, cierra })),
    );
    setEnviando(false);
  };

  return (
    <Paper variant="outlined" style={{ padding: 16, marginBottom: 16 }}>
      <Typography variant="subtitle1">{titulo}</Typography>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        {descripcion}
      </Typography>
      <Alert severity={texto ? "info" : "warning"} style={{ margin: "8px 0" }}>
        {texto
          ? `Vigente: ${texto}.`
          : "Sin días abiertos: este horario no restringe nada."}
      </Alert>
      {semana.map(({ dia, abierto, abre, cierra }) => (
        <Grid container spacing={2} alignItems="center" key={dia}>
          <Grid item xs={12} sm={4}>
            <FormControlLabel
              control={
                <Checkbox
                  color="primary"
                  checked={abierto}
                  onChange={({ target }) => cambiar(dia, "abierto", target.checked)}
                />
              }
              label={DIAS[dia].nombre}
            />
          </Grid>
          <Grid item xs={6} sm={4}>
            <TextField
              fullWidth
              type="time"
              size="small"
              label="Abre"
              InputLabelProps={{ shrink: true }}
              disabled={!abierto}
              value={abre}
              error={abierto && Boolean(abre) && abre >= cierra}
              onChange={({ target }) => cambiar(dia, "abre", target.value)}
            />
          </Grid>
          <Grid item xs={6} sm={4}>
            <TextField
              fullWidth
              type="time"
              size="small"
              label="Cierra"
              InputLabelProps={{ shrink: true }}
              disabled={!abierto}
              value={cierra}
              onChange={({ target }) => cambiar(dia, "cierra", target.value)}
            />
          </Grid>
        </Grid>
      ))}
      {malos.length > 0 && (
        <Typography variant="caption" color="error">
          La hora de apertura debe ser anterior a la de cierre.
        </Typography>
      )}
      <div style={{ textAlign: "right", marginTop: 8 }}>
        <Button color="primary" variant="contained" disabled={malos.length > 0 || enviando} onClick={guardar}>
          {enviando ? "Guardando..." : "Guardar"}
        </Button>
      </div>
    </Paper>
  );
};

TablaHorario.propTypes = {
  titulo: PropTypes.string.isRequired,
  descripcion: PropTypes.string.isRequired,
  texto: PropTypes.string,
  inicial: PropTypes.arrayOf(PropTypes.object).isRequired,
  onGuardar: PropTypes.func.isRequired,
};

TablaHorario.defaultProps = { texto: "" };

/**
 * Administrar el horario de solicitudes (2026-10-15), el mismo para Libro a
 * tu clase y ARIA: cuando se puede radicar y en que dias y horas se puede
 * pedir la actividad. Un dia sin marcar esta cerrado; un horario sin ningun
 * dia no restringe nada. El backend lo valida al crear y al editar.
 */
const HorarioSolicitudesGestion = ({ onMensaje, volver }) => {
  const [datos, setDatos] = useState(null);
  const [fallo, setFallo] = useState("");

  useEffect(() => {
    obtenerHorarioSolicitudesGestion()
      .then(setDatos)
      .catch((error) => setFallo(error.message));
  }, []);

  const guardar = (uso) => async (dias) => {
    const { ok, titulo, datos: nuevos } = await guardarHorarioSolicitudes(uso, dias);
    onMensaje(ok ? "Horario guardado." : titulo, ok ? "success" : "info");
    if (ok) setDatos(nuevos);
  };

  if (fallo) return <EstadoVacio mensaje={fallo} />;
  if (!datos) return <Cargando />;

  return (
    <div>
      <BotonAtras onClick={volver} />
      <Grid container justifyContent="center">
        <Grid item xs={12} md={10} lg={8}>
          {USOS.map(({ uso, titulo, descripcion }) => (
            <TablaHorario
              // Al guardar, la tabla vuelve a armarse con lo que quedo.
              key={`${uso}-${datos[uso].texto}`}
              titulo={titulo}
              descripcion={descripcion}
              texto={datos[uso].texto}
              inicial={datos[uso].dias}
              onGuardar={guardar(uso)}
            />
          ))}
        </Grid>
      </Grid>
    </div>
  );
};

HorarioSolicitudesGestion.propTypes = {
  onMensaje: PropTypes.func.isRequired,
  volver: PropTypes.func.isRequired,
};

export default HorarioSolicitudesGestion;
